import { useContext, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ActionIcon,
  Badge,
  Button,
  Flex,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  TextInput as MantineTextInput,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useForm } from "react-hook-form";
import { TextInput } from "react-hook-form-mantine";
import { web_upsertInviteRecipientSchema } from "@congrs/schemas";
import resolver from "@congrs/resolver";
import { IconEdit, IconSearch, IconSend, IconX } from "@tabler/icons-react";
import {
  useGetDistributionEmails,
  useGetDistributionEmailStats,
  useResendDistributionEmail,
  useUpdateDistributionEmailRecipient,
} from "../api/user";
import { DataTable } from "../components/DataTable";
import {
  TableAction,
  TableActions,
  actionsColumn,
} from "../components/TableActions";
import InviteStats from "../components/Registrations/Invites/InviteStats";
import { useConfirm } from "../hooks/useConfirm";
import { AuthContext } from "../services/context";
import { usePageTitle } from "../services/HeaderTitleContext";
import { getCurrentWorkspace } from "../services/userConfig";

const PAGE_SIZE = 10;

const STAT_ITEMS = [
  { key: "sent", label: "Sent", color: "blue" },
  { key: "delivered", label: "Delivered", color: "cyan" },
  { key: "opened", label: "Opened", color: "teal" },
  { key: "bounced", label: "Bounced", color: "red" },
  { key: "clicked", label: "Clicked", color: "green" },
];

const STATUS_CONFIG = {
  sending: { label: "Queued", color: "yellow" },
  sent: { label: "Sent", color: "blue" },
  delivered: { label: "Delivered", color: "cyan" },
  opened: { label: "Opened", color: "teal" },
  clicked: { label: "Clicked", color: "green" },
  bounced: { label: "Bounced", color: "red" },
};

const STATUS_OPTIONS = Object.entries(STATUS_CONFIG).map(
  ([value, { label }]) => ({ value, label })
);

export default function DocumentEmails() {
  const { user } = useContext(AuthContext);
  const eventId = getCurrentWorkspace(user)?.eventId;
  const distributionId = Number(useParams().distributionId);

  const { data: stats } = useGetDistributionEmailStats({
    eventId,
    distributionId,
  });

  return (
    <Flex direction="column" gap="lg">
      <InviteStats stats={stats} items={STAT_ITEMS} />
      <EmailsTable eventId={eventId} distributionId={distributionId} />
    </Flex>
  );
}

function EmailsTable({ eventId, distributionId }) {
  const [page, setPage] = useState(1);
  const [sorting, setSorting] = useState({
    columnAccessor: "email",
    direction: "asc",
  });
  const [filter, setFilter] = useState(null);
  const [debouncedFilter] = useDebouncedValue(filter, 200);
  const [editRecipient, setEditRecipient] = useState(null);
  const confirm = useConfirm();
  const { user: currentUser } = useContext(AuthContext);
  // A recipient is an account; only a sysAdmin may change someone else's.
  const canEditRecipients = currentUser?.role === "sysAdmin";

  const { data, isPending } = useGetDistributionEmails({
    eventId,
    distributionId,
    page,
    limit: PAGE_SIZE,
    sort: JSON.stringify(sorting),
    filter: debouncedFilter,
  });
  const { mutate: resend, isPending: isResending } = useResendDistributionEmail(
    { eventId, distributionId }
  );

  usePageTitle(data?.distributionFileName ?? null);

  const handleSortChange = (sort) => {
    setPage(1);
    setSorting(sort);
  };

  const handleFilterChange = (value) => {
    setPage(1);
    setFilter(value);
  };

  const resendOne = (row) =>
    confirm({
      title: "Resend email?",
      message: `Send ${row.email} a new link to their document. The link lasts 90 days.`,
      confirmLabel: "Resend",
      onConfirm: () => resend({ documentId: row.id }),
    });

  const searchColumn = (accessor, title) => ({
    accessor,
    title,
    sortable: true,
    render: (row) => row[accessor] || "-",
    filter: (
      <FilterSearch
        query={filter}
        setQuery={handleFilterChange}
        columnName={accessor}
      />
    ),
    filtering: filter?.[accessor],
  });

  const columns = [
    searchColumn("email", "Email"),
    searchColumn("firstName", "First name"),
    searchColumn("lastName", "Last name"),
    {
      accessor: "emailStatus",
      title: "Status",
      sortable: true,
      render: (row) => {
        const config = STATUS_CONFIG[row.emailStatus];
        return (
          <Badge color={config?.color ?? "gray"} variant="light">
            {config?.label ?? row.emailStatus}
          </Badge>
        );
      },
      filter: <StatusFilter query={filter} setQuery={handleFilterChange} />,
      filtering: filter?.emailStatus,
    },
    actionsColumn((row) => (
      <TableActions>
        {row.emailStatus !== "sending" && (
          <TableAction
            label="Resend"
            icon={IconSend}
            onClick={() => resendOne(row)}
            disabled={isResending}
          />
        )}
        {canEditRecipients && (
          <TableAction
            label="Edit"
            icon={IconEdit}
            onClick={() => setEditRecipient(row)}
          />
        )}
      </TableActions>
    )),
  ];

  return (
    <>
      <DataTable
        columns={columns}
        records={data?.data || []}
        fetching={isPending}
        page={page}
        onPageChange={setPage}
        totalRecords={data?.pagination?.total || 0}
        recordsPerPage={PAGE_SIZE}
        sortStatus={sorting}
        onSortStatusChange={handleSortChange}
      />
      <EditRecipientModal
        eventId={eventId}
        distributionId={distributionId}
        recipient={editRecipient}
        onClose={() => setEditRecipient(null)}
      />
    </>
  );
}

function EditRecipientModal({ eventId, distributionId, recipient, onClose }) {
  const { user } = useContext(AuthContext);
  const { control, handleSubmit } = useForm({
    values: {
      email: recipient?.email || "",
      firstName: recipient?.firstName || "",
      lastName: recipient?.lastName || "",
    },
    resolver: resolver(web_upsertInviteRecipientSchema),
  });

  const { mutate: updateRecipient, isPending } =
    useUpdateDistributionEmailRecipient({
      eventId,
      distributionId,
      documentId: recipient?.id,
    });

  const onSubmit = (data) =>
    updateRecipient(
      {
        email: data.email,
        firstName: data.firstName || null,
        lastName: data.lastName || null,
      },
      { onSuccess: onClose }
    );

  return (
    <Modal
      opened={!!recipient}
      onClose={onClose}
      title="Edit recipient"
      centered
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            This updates the attendee&apos;s account. Use Resend to email the
            document to the new address.
          </Text>
          <TextInput
            label="Email"
            name="email"
            control={control}
            placeholder="name@example.com"
            withAsterisk
            disabled={user?.role !== "sysAdmin"}
            styles={{
              input: { opacity: 1, color: "var(--mantine-color-text)" },
            }}
          />
          <TextInput
            label="First name"
            name="firstName"
            control={control}
            placeholder="Jane"
          />
          <TextInput
            label="Last name"
            name="lastName"
            control={control}
            placeholder="Doe"
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={isPending}>
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function FilterSearch({ query, setQuery, columnName }) {
  const readableColumnName = columnName
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase();
  return (
    <MantineTextInput
      label="Recipients"
      description={`Show recipients whose ${readableColumnName} includes the specified text`}
      placeholder="Search recipients..."
      leftSection={<IconSearch size={16} />}
      rightSection={
        <ActionIcon
          size="sm"
          variant="transparent"
          c="dimmed"
          onClick={() => setQuery(null)}
        >
          <IconX size={14} />
        </ActionIcon>
      }
      value={query?.[columnName] || ""}
      onChange={(e) => {
        if (e.currentTarget.value === "") {
          setQuery(null);
          return;
        }
        setQuery({ [columnName]: e.currentTarget.value });
      }}
    />
  );
}

function StatusFilter({ query, setQuery }) {
  return (
    <Select
      label="Status"
      description="Filter recipients by status"
      placeholder="Filter by status"
      data={STATUS_OPTIONS}
      value={query?.emailStatus || null}
      onChange={(value) => setQuery(value ? { emailStatus: value } : null)}
      clearable
    />
  );
}
